import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import moment from 'moment';
import {
  useNavigate,
} from 'react-router';
import {
  get,
  find,
  isEmpty,
  toNumber,
} from 'lodash';
import {
  API,
  GlobalActions,
  ToastActions,
  CurrencyActions,
  FlightSummaryModel,
  NavigationHelper,
  Field2 as Field,
  SidePanelContainer,
  SidePanelHeader,
  SidePanelFooter,
  SidePanelContent,
  SidePanelSection,
  Assignments,
  VibeTooltip,
  VibeButton,
  VibeAlert,
  VibeModal,
  VibeIcon,
  viClose,
  viCopy,
  viArchive,
  viUnarchive,
  color,
} from 'vibeguide';
import C, { lower } from 'constants';
import Information from './Information';
import SegmentCalculator from './SegmentCalculator';
import SegmentCPM from './SegmentCPM';
import AtAGlance from './AtAGlance';
import SegmentLocations from './SegmentLocations';
import SegmentFlights from './SegmentFlights';
import SegmentDateTime from './SegmentDateTime';
import SegmentMediaDayParts from './SegmentMediaDayParts';
import './SegmentDetails.scss';

function SegmentDetails({
  className = '',
  isNew = false,
  routeId = '',
  segment = {},
  flight = {},
  flightSummary = {},
  user,
  socket,
  currencies,
  setPanel,
  queueToast,
  getCurrencies,
  onUpdate = () => {},
}) {
  const [scenario, setScenario] = useState({
    _id: null,
    flightId: null,
    flightSummary: new FlightSummaryModel(),
  });
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [partnerSpotTypes, setPartnerSpotTypes] = useState([]);

  // Currency Code to use for the Segment (Flight takes precedence)
  const currencyCode = flight.currencyCode || segment.currencyCode;

  const history = useNavigate();
  const exceedsLocations = user.exceedsLocations(segment.locationSpec);

  const checkContinuousCreativeDates = () => {
    let returnValue = false;
    segment.dayParts.forEach((daypart) => {
      if (get(daypart, 'dateRanges.length', 0) > 0) {
        const firstEndDate = moment(daypart.dateRanges[0].endDate);
        daypart.dateRanges.forEach((dateRange, index) => {
          if (index > 0) {
            const startdate = moment(dateRange.startDate);
            returnValue = startdate.isBefore(firstEndDate);
          }
          return returnValue;
        });
      }
    });
    return returnValue;
  };

  /**
   * Translate Creative Weights
   */

  const translateCreativeWeights = (data, translateType) => {
    data.dayParts.forEach((dayPart) => {
      get(dayPart, 'dateRanges', []).forEach((daterange) => {
        if (!isEmpty(daterange.creatives)) {
          daterange.creatives.forEach((creative) => {
            if (creative) {
              creative.weight = translateType === 'divide'
                ? creative.weight /= 100 : Math.round((creative.weight * 100));
            }
          });
        }
      });
    });
  };

  const checkAllCreativeWeights = () => {
    let creativeWeightErrors = false;
    creativeWeightErrors = segment.dayParts.every((daypart) => {
      return get(daypart, 'dateRanges', []).every((daterange) => {
        if (isEmpty(daterange.creatives)) return true;
        const totalCreativeWeight = daterange.creatives.reduce((accumulator, creative) => {
          return accumulator + toNumber(creative.weight);
        }, 0) || 0;
        creativeWeightErrors = totalCreativeWeight !== 100;
        if (creativeWeightErrors) return false;
        return true;
      });
    });

    return creativeWeightErrors;
  };

  /**
   * Get Flight Summary
   */
  const getFlightSummary = async () => {
    const flightSummaryResponse = await API.Flight.summary(scenario.flightId);

    setScenario({
      ...scenario,
      flightSummary: new FlightSummaryModel(flightSummaryResponse),
    });
  };

  /**
   * Get the warning message for the locations assigned (if disabled)
   */
  const getLocationWarning = () => {
    const warning = [];

    if (!exceedsLocations) {
      warning.push('You do not have access to all the locations assigned to this object');
    }

    if (!segment.mediaFormat) {
      warning.push('You must choose a media format before selecting locations');
    }

    return warning.join(' ');
  };

  const getFlightWarning = () => {
    const warning = [];

    // only add show date requirement if segment has no proposal assigned to it
    if ((!segment.startDate || !segment.endDate) && !segment.flightId) {
      warning.push(`Please add a start and end date to select a ${lower(C.PROPOSAL)}`);
    }

    return warning.join(' ');
  };

  const onChangeCurrency = (e) => {
    const {
      name,
      value,
    } = e.target;

    onUpdate({
      [name]: value,
    });
  };

  const onClose = () => {
    setPanel({
      show: false,
    });
  };

  const onClickLocations = () => {
    // only allow location changes when if the flight is uconfirmed (or no flight)
    const allowChanges = (
      !flight._id
      || (flight._id && flight.status === 'unconfirmed' && flight.active)
    ) && (
      ((segment._id && user.can('flight_segment.modify'))
      || (!segment._id && user.can('flight_segment.create')))
    );

    setPanel({
      extraPanel: {
        width: window.innerWidth,
        show: true,
        children: (
          <SegmentLocations
            locations={segment.locationSpec}
            locationsData={segment.locationsData}
            mediaFormat={segment.mediaFormat}
            allowChanges={allowChanges}
            onUpdate={onUpdate}
          />
        ),
      },
    });
  };

  const onClickFlight = () => {
    setPanel({
      extraPanel: {
        width: window.innerWidth,
        show: true,
        children: (
          <SegmentFlights
            flightId={segment.flightId}
            segmentStartDate={segment.startDate}
            segmentEndDate={segment.endDate}
            segmentDayParts={segment.dayParts}
            onUpdate={onUpdate}
          />
        ),
      },
    });
  };

  /**
   * When the archive button is clicked
   */
  const onClickArchive = () => {
    setConfirmArchive(true);
  };

  /**
   * When the unarchive button is clicked
   */
  const onClickUnarchive = async () => {
    await API.Flight.Segment.reactivate({
      _id: segment._id,
      flightId: flight._id,
    });

    onClose();

    // tell listening components the segment was saved
    document.dispatchEvent(new Event('onSaveSegment'));
  };

  /**
   * When the archive modal is closed
   */
  const onCloseArchive = () => {
    setConfirmArchive(false);
  };

  /**
   * When the archive modal is confirmed
   */
  const onConfirmArchive = async () => {
    await API.Flight.Segment.deactivate({
      _id: segment._id,
      flightId: flight._id,
    });

    onCloseArchive();
    onClose();

    // tell listening components the segment was saved
    document.dispatchEvent(new Event('onSaveSegment'));
  };

  const onSave = async () => {
    // create a segment scenario when no flight ID is selected
    const isScenario = !segment.flightId;
    const qs = NavigationHelper.getParams();

    // default the dateRange for each dayPart that doesn't have one
    // use the segment start/end date as the date range dates
    const dayParts = segment.dayParts.map(dayPart => {
      if (dayPart.dateRanges.length <= 0) {
        dayPart.dateRanges.push({
          startDate: segment.startDate,
          endDate: segment.endDate,
        });
      }

      return {
        ...dayPart,
        // ensure each date range has a start/end date (if not, use the segment start/end dates)
        dateRanges: dayPart.dateRanges.map(dateRange => {
          return {
            ...dateRange,
            startDate: !dateRange.startDate
              ? segment.startDate
              : dateRange.startDate,
            endDate: !dateRange.endDate
              ? segment.endDate
              : dateRange.endDate,
          };
        }),
      };
    });

    const data = {
      name: segment.name,
      locations: segment.locations,
      mediaFormat: segment.mediaFormat,
      spotType: segment.spotType,
      spotSelectionStrategy: segment.spotSelectionStrategy,
      allocateRemainder: segment.allocateRemainder,
      allowBackToBackAdDelivery: segment.allowBackToBackAdDelivery,
      startDate: segment.startDate,
      endDate: segment.endDate,
      dayParts,
      daysOfWeek: segment.daysOfWeek,
      segmentType: segment.segmentType,
      currencyCode,
    };

    // If daypart only has default date range, update the start
    // and end dates to reflect the segment ones.
    // If ad provider is not vibenomics, remove creatives array.
    data.dayParts = segment.dayParts.map(dayPart => {
      if (dayPart.dateRanges.length === 1) {
        const correctedDayPart = { ...dayPart };
        correctedDayPart.dateRanges = dayPart.dateRanges.map(dateRange => {
          const correctedDateRange = { ...dateRange };
          if (flight.adProvider !== 'vibenomics' && correctedDateRange.creatives) {
            delete correctedDateRange.creatives;
          }
          correctedDateRange.startDate = segment.startDate;
          correctedDateRange.endDate = segment.endDate;
          return correctedDateRange;
        });
        return correctedDayPart;
      }
      return dayPart;
    });

    if (segment._id && qs.type !== 'new') {
      // editing a segment
      data._id = segment._id;
    }

    // only include CPM, Budget, Impressions if a value is provided
    if (segment.cpm > 0 && segment.segmentType !== 'added-value') {
      data.cpm = segment.cpm;
    }

    if (segment.budget > 0 && segment.segmentType !== 'added-value') {
      data.budget = segment.budget;
    }

    if (segment.spotSelectionStrategy !== 'spots-per-hour' && segment.frequencyCapped) {
      data.frequencyCapped = segment.frequencyCapped;
      data.frequencyCap = parseInt(segment.frequencyCap, 10);
    } else {
      data.frequencyCapped = false;
      data.frequencyCap = 0;
    }

    // When the calculator is locked, assuming all 3 fields are filled in, do not send impressions
    // unless the segment type is added-value then impressions are required
    if ((segment.segmentType === 'added-value' && segment.impressions > 0)
      || (segment.segmentType === 'standard' && segment.impressions > 0 && !segment.lockCalculator)
    ) {
      data.impressions = segment.impressions;
    }

    if (segment.spotSelectionStrategy === 'spots-per-hour') {
      data.spotsPerHour = segment.spotsPerHour;
    }

    // a flight is assigned to the segment
    if (!isScenario
      && qs.type !== 'new'
      && routeId !== segment.flightId
    ) {
      data.newFlightId = segment.flightId;
    }

    translateCreativeWeights(data, 'divide');

    const createFunc = data => {
      return isScenario
        ? API.Flight.Segment.createScenario(data)
        : API.Flight.Segment.create({ ...data, flightId: segment.flightId });
    };

    const successResponseType = isScenario
      ? 'FLIGHTSCENARIO.CREATED'
      : 'FLIGHTSEGMENT.CREATED';

    queueToast({
      type: 'info',
      title: 'Saving...',
      allowClose: true,
    });

    try {
      const response = segment._id && qs.type !== 'new'
        ? await API.Flight.Segment.update({ ...data, flightId: routeId })
        : await createFunc(data);

      const segmentId = get(response, '[0].documentId', null);
      const responseType = get(response, '[0].type');

      const success = data._id
        ? responseType === 'FLIGHTSEGMENT.UPDATED'
        : responseType === successResponseType;

      if (success) {
        // Successfully saved the segment
        const saveMessage = data._id
          ? 'Segment Updated!'
          : 'Segment Created!';

        // get the computed value
        const computedCampaignTriadValue = get(response, '[0].data.computedCampaignTriadValue');
        const computedValue = computedCampaignTriadValue
          ? get(response, `[0].data.${computedCampaignTriadValue}`)
          : null;

        if (computedValue) {
          // update the segment with the computed value
          onUpdate({
            [computedCampaignTriadValue]: computedValue,
            // lock the calculator so subsequent saves won't have an error
            lockCalculator: true,
          });
        }

        queueToast({
          type: 'success',
          title: saveMessage,
          allowClose: true,
        });

        // tell listening components the object was saved
        document.dispatchEvent(new Event('onSaveSegment'));

        // do not redirect when the segment is only a scenario
        if (!isScenario) {
          const redirectUrl = NavigationHelper.updateParams({
            segmentId,
            flightId: null,
            type: null,
          }, {
            pathname: `/ad-ops/${lower(C.PROPOSAL)}/${get(response, '[0].data.flightId', null)}/${lower(C.SEGMENTS)}`,
          });

          history(redirectUrl);
        } else {
          // segment is a scenario, need to listen for socket events
          setScenario({
            ...scenario,
            _id: get(response, '[0].documentId', null),
            flightId: get(response, '[0].data.flightId', null),
          });
        }

        translateCreativeWeights(data, 'multiply');
      } else {
        console.error('Error saving segment to API', response);

        queueToast({
          type: 'error',
          title: 'Error Saving Segment',
          timeout: 10,
          allowClose: true,
        });
      }
    } catch (err) {
      console.error('Error saving segment', err);

      translateCreativeWeights(data, 'multiply');

      queueToast({
        type: 'error',
        title: 'Error Saving Segment',
        timeout: 10,
        allowClose: true,
      });
    }
  };

  const onClickCalculate = () => {
    onSave();
  };

  /**
   * Listen for socket events
   * Only use for scenario's. SegmentSidebar handles regular segment events
   */
  const onApiEvent = (e) => {
    switch (e.type) {
      case 'FLIGHT.COMPUTED':
      case 'FLIGHT.RECOVERED': {
        if (scenario.flightId) {
          getFlightSummary();
        }
        break;
      }

      default:
        // console.warn('Event', e);
        break;
    }
  };

  /**
   * When the socket reconnects
   */
  const onSocketReconnect = () => {
    if (scenario.flightId) {
      socket.emit('subscribe', {
        topic: 'flight',
        id: scenario.flightId,
      });
      getFlightSummary();
    }
  };

  /**
   * Check disabling the calculate button
   */
  const checkDisableCalculate = () => {
    if (!segment.name
      || !segment.spotType
      || !segment.startDate
      || !segment.endDate
      // ensure each day part has a start/end time
      || segment.dayParts.find(dayPart => dayPart.startTime === null || dayPart.endTime === null)
      || segment.daysOfWeek.length <= 0
      || segment.locationCount <= 0
    ) {
      return true;
    }

    if (flight.orderType !== 'always-on-pmp' && (
      !segment.spotSelectionStrategy
      || (segment.spotSelectionStrategy === 'spots-per-hour'
        && (!segment.spotsPerHour && !segment.budget))
    )) {
      return true;
    }

    if (flight.orderType !== 'always-on-pmp' && segment.spotSelectionStrategy !== 'spots-per-hour') {
      // only allow one field in the "calculator" section to be empty
      let unsetFieldCount = 0;

      if (segment.cpm <= 0) {
        unsetFieldCount += 1;
      }

      if (segment.budget <= 0) {
        unsetFieldCount += 1;
      }

      if (segment.impressions <= 0) {
        unsetFieldCount += 1;
      }

      if ((unsetFieldCount > 0 && segment.segmentType === 'added-value')) {
        return false;
      }

      /**
       * more than 1 unset field
       * or all fields set and calculator is unlocked
       */
      if (unsetFieldCount > 1
        || (unsetFieldCount <= 0 && !segment.lockCalculator)
      ) {
        // require 2 fields to be filled in
        // more than one field in the "calculator" section is empty
        return true;
      }
    }

    return false;
  };

  /**
   * When connected to the socket, listen for flight events
   * Subscribe to the flight if calculating a scenario
   */
  useEffect(() => {
    socket.on('connect', onSocketReconnect);

    if (socket.connected) {
      socket.off('VAPI_EVENT', onApiEvent);
      socket.on('VAPI_EVENT', onApiEvent);

      if (scenario.flightId) {
        socket.emit('subscribe', {
          topic: 'flight',
          id: scenario.flightId,
        });
      }
    }

    // when component unmounts
    return () => {
      socket.off('VAPI_EVENT', onApiEvent);
      socket.off('connect', onSocketReconnect);

      if (scenario.flightId) {
        socket.emit('unsubscribe', {
          topic: 'flight',
          id: scenario.flightId,
        });
      }
    };
  }, [socket.connected, scenario.flightId]);

  /**
   * When it's a new segment, allow duplicate ads back 2 back
   */
  useEffect(() => {
    if (isNew) {
      onUpdate({
        allowBackToBackAdDelivery: true,
        // default the currency code to the users default (if no flight currency is selected)
        currencyCode: flight.currencyCode || user.currencyCode,
      });
    }

    // Fetch the Partner Ad Config to determine which Spot Types to show
    if (user.partnerId) {
      async function fetchData() {
        const adConfig = await API.Partner.getAdConfig({
          _id: user.partnerId,
        });

        setPartnerSpotTypes(adConfig.spotTypes);
      }
      fetchData();
    }

    // Fetch the supported currencies
    getCurrencies();
  }, []);

  const segmentSummary = !scenario.flightId
    // segment summary from a regular flight
    ? find(flightSummary.segments, { _id: segment._id }) || {}
    // segment summary from a scenario
    : find(scenario.flightSummary.segments, { _id: scenario._id }) || {};

  const disableCalculate = checkDisableCalculate();
  const disableContinuousCreativeDates = checkContinuousCreativeDates();
  const disableCreativeWeight = !checkAllCreativeWeights();

  const disableInput = (flight._id && flight.status !== 'unconfirmed')
    || (flight._id && !flight.active)
    || !exceedsLocations
    || (segment._id && !user.can('flight_segment.modify'))
    || (!segment._id && !user.can('flight_segment.create'));

  // do not allow saving without a flight assignment
  const disableSave = disableInput
    || disableCalculate
    || disableContinuousCreativeDates
    || disableCreativeWeight
    || !segment.flightId
    || !currencyCode;

  const canPartMultiple = flight.adProvider === 'vibenomics';

  const disableMedia = !flight._id
    || flight.adProvider !== 'vibenomics'
    || disableInput;

  const showArchive = user.can('flight_segment.delete');
  const isArchived = !segment.active;

  // disable currency selection if the flight has a currency associated with it
  const disableCurrency = !isEmpty(flight.currencyCode);

  return (
    <SidePanelContainer className={classNames('SegmentDetails', className)}>
      <SidePanelHeader
        icons={(
          <VibeIcon
            className="close"
            icon={viClose}
            color={color.manatee}
            hoverColor={color.obsidian}
            size={24}
            onClick={onClose}
            data-testid="segment-close-button"
          />
        )}
      >
        {segment._id ? (
          <div className="flex-horizontal">
            {get(segmentSummary, 'warnings.length', 0) > 0 && (
              <VibeAlert
                style={{
                  marginRight: 8,
                  fontSize: 12,
                }}
                severity="warning"
                alerts={segmentSummary.warnings || []}
                size={24}
                iconOnly
              />
            )}

            <div className="title">
              <VibeTooltip title={segment.name}>
                <span>
                  {segment.name}
                </span>
              </VibeTooltip>
            </div>
          </div>
        ) : (
          <div className="title">
            New {C.SEGMENT}
          </div>
        )}
      </SidePanelHeader>

      <SidePanelContent>
        <SidePanelSection title="Information">
          <Information
            orderType={flight.orderType}
            name={segment.name}
            mediaFormat={segment.mediaFormat}
            adProvider={flight.adProvider}
            locationSpec={segment.locationSpec}
            spotType={segment.spotType}
            partnerSpotTypes={partnerSpotTypes}
            spotSelectionStrategy={segment.spotSelectionStrategy}
            allocateRemainder={segment.allocateRemainder}
            segmentType={segment.segmentType}
            startDate={segment.startDate}
            endDate={segment.endDate}
            startTime={segment.startTime}
            endTime={segment.endTime}
            daysOfWeek={segment.daysOfWeek}
            frequencyCap={segment.frequencyCap}
            frequencyCapped={segment.frequencyCapped}
            allowBackToBackAdDelivery={segment.allowBackToBackAdDelivery || false}
            disableInput={disableInput}
            onUpdate={onUpdate}
          />
        </SidePanelSection>

        {flight.orderType !== 'always-on-pmp' ? (
          <SidePanelSection>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 64,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 'bold',
                }}
              >
                Calculator
              </div>

              <div
                style={{
                  flexGrow: 1,
                }}
              >
                <Field
                  type="select"
                  name="currencyCode"
                  value={currencyCode}
                  options={[
                    {
                      label: '',
                      value: '',
                      placeholder: 'Choose a Currency...',
                    },
                    ...currencies.map(currency => {
                      return {
                        label: currency.label,
                        value: currency.key,
                      };
                    }),
                  ]}
                  disabled={disableInput || disableCurrency}
                  testId="segment-calculator-currency-select"
                  onChange={onChangeCurrency}
                />
              </div>
            </div>

            <SegmentCalculator
              currencyCode={currencyCode}
              cpm={segment.cpm}
              budget={segment.budget}
              impressions={segment.impressions}
              spotsPerHour={segment.spotsPerHour}
              segmentType={segment.segmentType}
              locked={segment.lockCalculator && !disableInput}
              spotSelectionStrategy={segment.spotSelectionStrategy}
              disableInput={disableInput}
              onUpdate={onUpdate}
            />
          </SidePanelSection>
        ) : (
          <SidePanelSection>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 64,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 'bold',
                }}
              >
                CPM
              </div>

              <div
                style={{
                  flexGrow: 1,
                }}
              >
                <Field
                  type="select"
                  name="currencyCode"
                  value={currencyCode}
                  options={[
                    {
                      label: '',
                      value: '',
                      placeholder: 'Choose a Currency...',
                    },
                    ...currencies.map(currency => {
                      return {
                        label: currency.label,
                        value: currency.key,
                      };
                    }),
                  ]}
                  disabled={disableInput || disableCurrency}
                  testId="segment-cpm-currency-select"
                  onChange={onChangeCurrency}
                />
              </div>
            </div>

            <SegmentCPM
              cpm={segment.cpm}
              cpmType={segment.cpmType}
              currencyCode={currencyCode}
              disableInput={disableInput}
              onUpdate={onUpdate}
            />
          </SidePanelSection>
        )}

        <Assignments
          items={[{
            label: 'Locations Assigned',
            count: segment.locationCount,
            required: true,
            // media format must be selected (and the objects location scope doesn't exceed the users)
            disabled: !exceedsLocations || !segment.mediaFormat,
            warning: getLocationWarning(),
            onClick: exceedsLocations && segment.mediaFormat
              ? onClickLocations
              : null,
          },
          {
            label: `${C.PROPOSAL}`,
            count: flight.name || `Choose a ${C.PROPOSAL}...`,
            tooltip: flight.name,
            required: true,
            disabled: !exceedsLocations
              || disableInput
              || !segment.startDate
              || !segment.endDate,
            warning: getFlightWarning(),
            // only show flight table selector if the user can modify the segment
            // and the selected locations don't exceed their location scope
            // and the segment has start & end dates
            onClick: exceedsLocations
              && !disableInput
              && segment.startDate
              && segment.endDate
              ? onClickFlight
              : null,
          }]}
        />

        <SidePanelSection title="Media &amp; Day Parts">
          <SegmentDateTime
            startDate={segment.startDate}
            endDate={segment.endDate}
            daysOfWeek={segment.daysOfWeek}
            disableInput={disableInput}
            onUpdate={onUpdate}
          />

          <SegmentMediaDayParts
            mediaFormat={segment.mediaFormat}
            advertiserId={flight.advertiserId}
            segmentLocations={segment.locations}
            startDate={segment.startDate}
            endDate={segment.endDate}
            dayParts={segment.dayParts}
            canPartMultiple={canPartMultiple}
            disableInput={disableInput}
            disableMedia={disableMedia}
            showMedia={flight.adProvider === 'vibenomics'}
            onUpdate={onUpdate}
          />
        </SidePanelSection>

        {/* Show at a glance with regular flight summary data or scenario summary data */}
        {(((segment._id && flight.active) || scenario._id)) && (
          <AtAGlance
            flightSummary={scenario._id
              ? scenario.flightSummary
              : flightSummary}
            segmentSummary={segmentSummary}
          />
        )}
      </SidePanelContent>

      <SidePanelFooter className="panel-footer">
        <VibeButton
          text="Save Segment"
          style={{
            marginRight: 12,
          }}
          color="secondary"
          // loadingEvent="onSaveSegment"
          disabled={disableSave || disableInput}
          testId="segment-save-button"
          onClick={onSave}
        />

        {!segment._id && (
          <VibeButton
            text="Calculate"
            color="secondary"
            tooltip={(
              <div>
                Run to get detailed information about how this segment would perform based on the current configuration.

                <div
                  style={{
                    marginTop: 8,
                  }}
                >
                  The &quot;At a Glance&quot; section will update automatically when complete.
                </div>
              </div>
            )}
            disabled={disableCalculate || disableInput}
            testId="segment-calculate-button"
            onClick={onClickCalculate}
          />
        )}

        {segment._id && user.can('flight_segment.create') && (
          <VibeIcon
            icon={viCopy}
            type="button"
            buttonProps={{
              size: 32,
              borderColor: color.primary,
            }}
            color={color.primary}
            size={16}
            disabled={disableInput}
            tooltip="Duplicate"
            link={NavigationHelper.updateParams({
              type: 'new',
            })}
            data-testid="segment-duplicate-button"
          />
        )}

        {showArchive && !isArchived && (
          <div className="toolbar-button">
            <VibeIcon
              icon={viArchive}
              type="button"
              buttonProps={{
                size: 32,
                borderColor: color.error,
              }}
              tooltip="Archive"
              color={color.error}
              size={20}
              disabled={disableInput}
              onClick={onClickArchive}
              data-testid="segment-archive-button"
            />
          </div>
        )}

        {showArchive && isArchived && !isNew && (
          <div className="toolbar-button">
            <VibeIcon
              icon={viUnarchive}
              type="button"
              buttonProps={{
                size: 32,
                borderColor: color.success,
              }}
              tooltip="Unarchive"
              color={color.success}
              size={20}
              disabled={disableInput}
              onClick={onClickUnarchive}
              data-testid="segment-unarchive-button"
            />
          </div>
        )}
      </SidePanelFooter>

      <VibeModal
        show={confirmArchive}
        type="confirm"
        title="Archive Segment"
        text={`Are you sure you want to archive ${segment.name}?`}
        confirmProps={{
          color: 'error',
        }}
        testIds={{
          root: 'archive-segment-dialog',
          confirm: 'archive-segment-confirm-button',
          cancel: 'archive-segment-cancel-button',
        }}
        onConfirm={onConfirmArchive}
        onClose={onCloseArchive}
      />
    </SidePanelContainer>
  );
}

function mapStateToProps(state) {
  return {
    user: state.login.user,
    socket: state.socket.connection,
    currencies: state.currency.list,
  };
}

const mapDispatchToProps = {
  setPanel: GlobalActions.setPanel,
  queueToast: ToastActions.queueToast,
  getCurrencies: CurrencyActions.getCurrencies,
};

export default connect(mapStateToProps, mapDispatchToProps)(SegmentDetails);
